/** @jsxImportSource @emotion/react */
import React from "react";
import { Button, message, Switch } from "antd";
import {
  LayoutContent,
  TypographySmall,
  LayoutContentFilterBar,
  SEO,
} from "../../../components";
import { Constant } from "../../../common/constant";
import {
  DataTable,
  ColumnConfig,
  DisplayColumns,
  Toolbox,
  useColumnVisibility,
} from "../../../components/DataTable";
import { FilterItem } from "../../../components/LayoutContentFilterBar";
import {
  TenantUserUsecase,
  useTenantUserDispatch,
  useTenantUserState,
} from "../../../usecases/TenantUser";
import {
  FilterParams,
  ModalTenantUserFilter,
} from "../../../components/TenantUser/ModalTenantUserFilter";
import Styles from "./styles";
import { Images } from "../../../common/images";
import { ModalAddTenantUser } from "../../../components/TenantUser/ModalAddTenantUser";
import { ModalDeleteTenantUser } from "../../../components/TenantUser/ModalDeleteTenantUser";
import { GetTenantUserDto } from "../../../services/TenantUser/dto/get.dto";
import { ModalEditTenantUser } from "../../../components/TenantUser/ModalEditTenantUser";
import { useAuthState } from "../../../usecases/Auth";

const TenantUserComponent: React.FC = () => {
  const authState = useAuthState();

  const TenantUserState = useTenantUserState();
  const TenantUserDispatch = useTenantUserDispatch();

  const [openNewTenantUserModal, setOpenNewTenantUserModal] =
    React.useState<boolean>(false);
  const [openEditTenantUserModal, setOpenEditTenantUserModal] =
    React.useState<boolean>(false);
  const [openDeleteTenantUserModal, setOpenDeleteTenantUserModal] =
    React.useState<boolean>(false);
  const [openFilterModal, setOpenFilterModal] = React.useState<boolean>(false);
  const [selectedTenantUser, setSelectedTenantUser] =
    React.useState<GetTenantUserDto>();
  const [filter, setFilter] = React.useState<FilterParams>({
    role: Constant.role.all,
  });

  const filterArray: FilterItem[] = [];
  const filterObject = filter;
  for (const key in filter) {
    if (key === "keyword" || !filterObject[key]) {
      continue;
    }
    let label = key.replace(/([A-Z])/g, " $1");
    label = label.charAt(0).toUpperCase() + label.slice(1);

    filterArray.push({
      label,
      key,
      value: filterObject[key],
    });
  }

  const onItemStatusChangeHandle =
    (value: GetTenantUserDto) => async (checked: boolean) => {
      if (TenantUserDispatch) {
        const tenantUserUsecase = new TenantUserUsecase(TenantUserDispatch);
        await tenantUserUsecase.updateStatus({
          id: value.id,
          isActive: checked,
        });
      }
    };

  const onModalAddTenantUserSuccessHandle = () => {
    setOpenNewTenantUserModal(false);
    message.success("Created new System User successful");
  };

  const onItemEditClickHandle = (value: GetTenantUserDto) => () => {
    setSelectedTenantUser(value);
    setOpenEditTenantUserModal(true);
  };

  const onModalEditTenantUserCancelHandle = () => {
    setOpenEditTenantUserModal(false);
    setSelectedTenantUser(undefined);
  };

  const onModalEditTenantUserSuccessHandle = () => {
    setOpenEditTenantUserModal(false);
    setSelectedTenantUser(undefined);
    message.success("Updated system user successful");
  };

  const onModalDeleteTenantUserCancelHandle = () => {
    setSelectedTenantUser(undefined);
    setOpenDeleteTenantUserModal(false);
  };

  const onModalDeleteTenantUserSuccessHandle = () => {
    setSelectedTenantUser(undefined);
    setOpenDeleteTenantUserModal(false);
    message.success("Deleted System User successful");
  };

  const onButtonDeleteClickHandle = (item: GetTenantUserDto) => () => {
    setSelectedTenantUser(item);
    setOpenDeleteTenantUserModal(true);
  };

  const onFilterItemCloseHandle = (item: FilterItem) => {
    const filterClone = {
      ...filter,
    };
    for (const key in filter) {
      if (key === item.key) {
        delete filterClone[key];
        break;
      }
    }
    setFilter(filterClone);
  };

  const onModalTenantUserFilterSearchHandle = (newFilter: FilterParams) => {
    setFilter(newFilter);
    setOpenFilterModal(false);
  };

  const onSearchHandle = (keyword: string) => {
    setFilter((prev) => ({ ...prev, keyword }));
  };

  React.useEffect(() => {
    async function asyncLoadData() {
      if (TenantUserDispatch) {
        const tenantUserUsecase = new TenantUserUsecase(TenantUserDispatch);
        await tenantUserUsecase.getList(filter);
      }
    }

    asyncLoadData();
  }, [TenantUserDispatch, filter]);

  const columns: ColumnConfig<GetTenantUserDto>[] = [
    {
      key: "userFirstName",
      title: "First name",
      type: "plain",
      sortable: true,
    },
    { key: "userLastName", title: "Surname", type: "plain", sortable: true },
    {
      key: "userEmail",
      title: "Email",
      type: "plain",
      width: 240,
      sortable: true,
    },
    {
      key: "userRole",
      title: "User type",
      type: "plain",
      filterable: true,
      sortable: true,
    },
    {
      key: "isActive",
      title: "Status",
      type: "status",
      sortable: true,
      render: function StatusCell(_value, record) {
        if (record.status !== "Activated") {
          return <img src={Images.WAITING_STA} css={Styles.waitingIconStyle} />;
        }
        if (record.id === authState.userInfo?.username) {
          return <Switch checked={record.isActive} disabled />;
        }
        return (
          <Switch
            onChange={onItemStatusChangeHandle(record)}
            checked={record.isActive}
          />
        );
      },
    },
    {
      key: "edit",
      title: "",
      tooltip: false,
      type: "plain",
      render: function EditCell(_value, record) {
        if (record.status !== "Activated") {
          return null;
        }
        return (
          <Button type="link" onClick={onItemEditClickHandle(record)}>
            <TypographySmall>Edit</TypographySmall>
          </Button>
        );
      },
    },
    {
      key: "delete",
      title: "",
      tooltip: false,
      type: "plain",
      render: function DeleteCell(_value, record) {
        return (
          <Button type="link" onClick={onButtonDeleteClickHandle(record)}>
            <TypographySmall type="danger">Delete</TypographySmall>
          </Button>
        );
      },
    },
  ];

  const toggleColumns = columns.filter((column) => column.title !== "");

  const getDisplayColumns = () => {
    const tenantUserUsecase = new TenantUserUsecase();
    return tenantUserUsecase.getDisplayColumns();
  };

  const setDisplayColumns = (displayColumns: DisplayColumns) => {
    const tenantUserUsecase = new TenantUserUsecase();
    tenantUserUsecase.setDisplayColumns(displayColumns);
  };

  const visibility = useColumnVisibility(
    toggleColumns,
    getDisplayColumns,
    setDisplayColumns,
  );

  const hasVisibleColumn = toggleColumns.some(
    (column) => visibility.visibility[column.key] !== false,
  );
  const tableVisibility = {
    ...visibility,
    visibility: {
      ...visibility.visibility,
      edit: hasVisibleColumn,
      delete: hasVisibleColumn,
    },
  };

  return (
    <LayoutContent
      title="Users"
      toolbar={
        <Toolbox>
          <Toolbox.Search onSearch={onSearchHandle} placeholder="Keyword" />
          <Toolbox.ColumnToggle {...visibility} />
          <Button
            type="primary"
            size="large"
            onClick={() => setOpenNewTenantUserModal(true)}
          >
            <img src={Images.ADD} css={Styles.iconStyle} />
            <span>Add New</span>
          </Button>
        </Toolbox>
      }
    >
      <SEO title="Home Builder Users" />
      <ModalAddTenantUser
        visible={openNewTenantUserModal}
        onCancel={() => setOpenNewTenantUserModal(false)}
        onSuccess={onModalAddTenantUserSuccessHandle}
      />
      {selectedTenantUser && openEditTenantUserModal && (
        <ModalEditTenantUser
          visible={openEditTenantUserModal}
          onCancel={onModalEditTenantUserCancelHandle}
          onSuccess={onModalEditTenantUserSuccessHandle}
          item={selectedTenantUser}
        />
      )}
      {selectedTenantUser && openDeleteTenantUserModal && (
        <ModalDeleteTenantUser
          visible={openDeleteTenantUserModal}
          onCancel={onModalDeleteTenantUserCancelHandle}
          onSuccess={onModalDeleteTenantUserSuccessHandle}
          item={selectedTenantUser}
        />
      )}
      <ModalTenantUserFilter
        visible={openFilterModal}
        onCancel={() => setOpenFilterModal(false)}
        onSearch={onModalTenantUserFilterSearchHandle}
        filter={filter}
      />
      <LayoutContentFilterBar
        onFilterClick={() => setOpenFilterModal(true)}
        onFilterClose={onFilterItemCloseHandle}
        filters={filterArray}
      />
      <DataTable
        rowKey="id"
        dataSource={TenantUserState.data}
        columnConfigs={columns}
        columnToggle
        visibility={tableVisibility}
      />
    </LayoutContent>
  );
};

export const TenantUser = React.memo(TenantUserComponent);
